import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FileService, FilesResponse } from '../../../services/file.service';
import { BreadcrumbComponent, BreadcrumbScope } from '../breadcrumb/breadcrumb.component';
import { MindmapViewerComponent, MindmapFormat } from '../mindmap-viewer/mindmap-viewer.component';
import { DownloadButtonComponent } from '../download-button/download-button.component';
import { LanguageToggleComponent } from '../language-toggle/language-toggle.component';

type ViewportSize = 'phone' | 'tablet' | 'laptop' | 'desktop';

@Component({
  selector: 'app-post-featured',
  standalone: true,
  imports: [CommonModule, BreadcrumbComponent, MindmapViewerComponent, DownloadButtonComponent, LanguageToggleComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <article class="featured" [attr.data-density]="density" [attr.lang]="lang">
      <header class="featured__head">
        <div class="featured__topRow">
          <span class="featured__pill" *ngIf="density !== 'compact'">
            <span class="material-icons" aria-hidden="true">star</span>
            Cover story
          </span>
          <app-breadcrumb [bundle]="bundle" (navigate)="crumbNavigate.emit($event)"></app-breadcrumb>
          <span class="featured__topRowSpacer"></span>
          <app-language-toggle [bundle]="bundle" [current]="lang" (langChange)="onLangChange($event)"></app-language-toggle>
        </div>
        <h1 class="featured__title">{{ bundle.topic || bundle.subtopic || 'Untitled' }}</h1>
        <p class="featured__sub" *ngIf="bundle.subtopic && bundle.subtopic !== bundle.topic">{{ bundle.subtopic }}</p>
        <div class="featured__metaRow" *ngIf="density === 'cover'">
          <span class="featured__date" *ngIf="dateLabel">
            <span class="material-icons" aria-hidden="true">schedule</span>
            {{ dateLabel }}
          </span>
        </div>
      </header>

      <app-mindmap-viewer
        [bundle]="bundle"
        [formats]="formats"
        [lang]="lang"
        [viewportSize]="viewportSize"
        preload="eager">
      </app-mindmap-viewer>

      <footer class="featured__foot">
        <div class="featured__downloads">
          <app-download-button *ngIf="canDownload('png')" [bundle]="bundle" fileType="png" [lang]="lang" label="PNG"></app-download-button>
          <app-download-button *ngIf="canDownload('pdf')" [bundle]="bundle" fileType="pdf" [lang]="lang" label="PDF"></app-download-button>
        </div>
        <button *ngIf="collapsible"
                type="button"
                class="featured__collapse"
                (click)="collapse.emit()"
                aria-label="Collapse">
          <span class="material-icons" aria-hidden="true">expand_less</span>
          <span>Collapse</span>
        </button>
      </footer>
    </article>
  `,
  styleUrls: ['./post-featured.component.css'],
})
export class PostFeaturedComponent {
  @Input({ required: true }) bundle!: FilesResponse;
  @Input() lang: 'en' | 'hi' = 'en';
  @Input() viewportSize: ViewportSize = 'laptop';
  @Input() density: 'cover' | 'comfortable' | 'compact' = 'comfortable';
  @Input() collapsible = false;

  @Output() collapse = new EventEmitter<void>();
  @Output() crumbNavigate = new EventEmitter<{ scope: BreadcrumbScope; bundle: FilesResponse }>();
  @Output() langChange = new EventEmitter<{ bundleId: number; lang: 'en' | 'hi' }>();

  private fileService = inject(FileService);

  get formats(): MindmapFormat[] {
    return this.fileService.availableFormats(this.bundle, this.lang);
  }

  canDownload(t: 'png' | 'pdf'): boolean {
    return !!this.fileService.resolveFileKey(this.bundle, t, this.lang);
  }

  get dateLabel(): string | null {
    const d = this.bundle.files?.['html']?.created_at ?? this.bundle.files?.['pdf']?.created_at;
    if (!d) return null;
    const date = new Date(d);
    if (isNaN(date.getTime())) return null;
    const days = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (days <= 0) return 'Published today';
    if (days === 1) return 'Published yesterday';
    if (days < 7)   return `Published ${days} days ago`;
    return 'Published ' + date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  }

  onLangChange(lang: 'en' | 'hi'): void {
    this.langChange.emit({ bundleId: this.bundle.id, lang });
  }
}
